import React from "react";
import Layout from "../Layout";
import { PageSubheader } from "../PageSubheader";
import { useParams } from "react-router";
import {
  GetQueryTableDataQueryVariables,
  SavedView as SavedViewType,
  useGetQueryTableDataLazyQuery,
  useGetSavedViewQuery,
  useGetTableConfigQuery,
} from "../../../generated/graphql";
import { FullWidthTable } from "../../Common/FullWidthTable/FullWidthTable";
import { setTitle } from "../../../utils/title";
import {
  initializeColumns,
  mergeFieldGroups,
  MinimalColumnDef,
} from "../../Common/FullWidthTable/utils";
import {
  useManualPaginationConfig,
  useQueryDescription,
} from "../../Common/Tables/hooks";
import { ColumnDef } from "@tanstack/react-table";
import { QueryPropertyTableResult } from "../../Property/__queries__/table";
import { QuerySubmissionTableResult } from "../../Submissions/__queries__/table";
import { QueryFileTableResult } from "../../DocumentUploads/__queries__/table";
import { buildColumnDefinitions } from "../../Common/Tables/utils";
import {
  PROPERTIES_TABLE_REQUIRED_FIELDS,
  PROPERTY_TABLE_CUSTOM_CELLS,
} from "../../Property/PropertiesTable";
import {
  FILES_TABLE_CUSTOM_CELLS,
  FILES_TABLE_REQUIRED_FIELDS,
} from "../../DocumentUploads/FilesTable";
import {
  SUBMISSION_TABLE_CUSTOM_CELLS,
  SUBMISSIONS_TABLE_REQUIRED_FIELDS,
} from "../../Submissions/SubmissionsTables";
import { TABLE_TYPE } from "common/constants";
import { TABLE_NAMES } from "common/utils/queryBuilder";
import {
  ACTION_COLUMN_DEF_CONSTANTS,
  ActionCell,
} from "../../Common/ActionCell";
import { ActionsProps } from "../../Inputs/DropdownMenu";
import { buildLink } from "common/routing";
import { NetworkStatus } from "@apollo/client";
import { lowerCase } from "lodash";
import { Attribute } from "../../Common/FullWidthTable/types";
import { MarkdownContainer } from "../../Common/__styles__/MarkdownContainer";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import { accountTitle } from "../utils";
import { Loading } from "../__styles__/Guest";

const REQUIRED_FIELDS = (tableName: TABLE_NAMES) => {
  switch (tableName) {
    case TABLE_NAMES.PROPERTIES:
      return PROPERTIES_TABLE_REQUIRED_FIELDS;
    case TABLE_NAMES.FILES:
      return FILES_TABLE_REQUIRED_FIELDS;
    case TABLE_NAMES.INSPECTIONS:
    case TABLE_NAMES.PERMITS:
      return SUBMISSIONS_TABLE_REQUIRED_FIELDS(tableName);
    default:
      return [];
  }
};

const SavedView = ({ savedView }: { savedView: SavedViewType }) => {
  const { data: columnConfigResponse, loading: loadingColumnConfig } =
    useGetTableConfigQuery({
      variables: { tableType: savedView.tableType },
      fetchPolicy: "network-only",
    });

  const [
    querySavedView,
    {
      previousData,
      data: currentData,
      networkStatus,
      loading: loadingQuerySavedView,
      error,
    },
  ] = useGetQueryTableDataLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  setTitle(`${savedView.name} | ${accountTitle()}`);

  const {
    initialTableState,
    queryDescription,
    updateQueryDescription,
    currentView,
  } = useQueryDescription({
    savedViews: [savedView],
    defaultSort: [],
  });

  const viewTableType = savedView.tableType;
  let tableName: TABLE_NAMES;
  switch (viewTableType) {
    case TABLE_TYPE.PROPERTIES:
      tableName = TABLE_NAMES.PROPERTIES;
      break;
    case TABLE_TYPE.INSPECTIONS:
      tableName = TABLE_NAMES.INSPECTIONS;
      break;
    case TABLE_TYPE.PERMITS:
      tableName = TABLE_NAMES.PERMITS;
      break;
    case TABLE_TYPE.FILES:
      tableName = TABLE_NAMES.FILES;
      break;
  }

  const noun = lowerCase(tableName);
  const loadingDetails = {
    loading: loadingQuerySavedView,
    loadingText: `Loading ${noun}`,
    noDataText: `No ${noun} found`,
  };

  const generatedColumnDefinitions = buildColumnDefinitions({
    columnConfig: columnConfigResponse?.getTableConfig.data ?? [],
    ignoreDefaultSize: true,
    cellRenderers: {
      ...PROPERTY_TABLE_CUSTOM_CELLS,
      ...FILES_TABLE_CUSTOM_CELLS,
      ...SUBMISSION_TABLE_CUSTOM_CELLS({ tableName }),
    },
  });

  const actionsColumn: MinimalColumnDef<
    [{ table: TABLE_NAMES; name: string }]
  > = {
    ...ACTION_COLUMN_DEF_CONSTANTS,
    cell: ({ row }) => {
      return (
        <ActionButton
          id={row.original[`${tableName}.id`]}
          tableType={viewTableType}
        />
      );
    },
  };

  const manualPaginationConfig = useManualPaginationConfig({
    ...initialTableState.pagination,
    currentTotalPages: currentData?.queryTableData.pageInfo.totalPages,
    previousTotalPages: previousData?.queryTableData.pageInfo.totalPages,
  });

  const tanstackColumnDefinitions: Array<
    ColumnDef<
      | QueryPropertyTableResult
      | QuerySubmissionTableResult
      | QueryFileTableResult
    >
  > = [...generatedColumnDefinitions, actionsColumn];

  const initialColumns = initializeColumns({
    initialTableState,
    tanstackColumnDefinitions,
    initialQueryDescription: queryDescription,
    actionsColumn,
  });

  if (loadingColumnConfig || !columnConfigResponse) {
    return null;
  }

  const tableConfig = columnConfigResponse.getTableConfig.data;

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  const timeoutError = error?.graphQLErrors.find(
    e => e.extensions.code === "TIMEOUT_ERROR"
  );

  const search = ({ page }: { page: number }) => {
    const variables: GetQueryTableDataQueryVariables = {
      description: {
        table: tableName,
        fields: mergeFieldGroups(
          queryDescription.fields,
          REQUIRED_FIELDS(tableName)
        ),
        orderBy: queryDescription.orderBy,
        filters: queryDescription.filters,
      },
      page,
    };

    void querySavedView({
      variables,
    });
  };

  return (
    <Layout hasGutters>
      <PageSubheader title={savedView.name} showDivider={false}>
        {savedView.description ? (
          <MarkdownContainer markdown={savedView.description} />
        ) : null}
      </PageSubheader>
      <FullWidthTable
        columns={initialColumns}
        previousData={previousData?.queryTableData.data}
        currentData={data?.queryTableData.data ?? []}
        loadingDetails={loadingDetails}
        tableStyleDetails={{ hasHighlights: true, hasRowActions: true }}
        manualPaginationConfig={{
          ...manualPaginationConfig,
          pageCount: data?.queryTableData.pageInfo.totalPages ?? 1,
        }}
        initialState={initialTableState}
        filterable={{
          newFilterConfiguration: tableConfig as Array<Attribute>,
          search,
        }}
        timeoutError={timeoutError}
        queryDescription={queryDescription}
        currentView={currentView}
        updateQueryDescription={updateQueryDescription}
        refetchSavedViews={() => {}}
        clientSideColumnConfig={tableConfig}
        dataSourceComputationsAffectingQuery={
          data?.queryTableData.dataSourceComputationsAffectingQuery
        }
        interactiveHeaders={false}
      />
    </Layout>
  );
};

const ActionButton = ({
  id,
  tableType,
}: {
  id: string;
  tableType: TABLE_TYPE;
}) => {
  let href: string;

  switch (tableType) {
    case TABLE_TYPE.PROPERTIES:
      href = buildLink("residentProperty", { propertyId: id });
      break;
    case TABLE_TYPE.INSPECTIONS:
    case TABLE_TYPE.PERMITS:
      href = buildLink("viewSubmission", { submissionId: id });
      break;
    case TABLE_TYPE.FILES:
      href = buildLink("guestDocumentUploadFile", { id });
      break;
  }

  const actions: Array<ActionsProps> = [
    {
      label: "View",
      href,
      target: "_blank",
    },
  ];
  return <ActionCell actions={actions} />;
};

export default () => {
  const { savedViewId } = useParams<{ savedViewId: string }>();

  const { data, loading: loadingSavedView } = useGetSavedViewQuery({
    variables: { savedViewId },
  });

  if (loadingSavedView) {
    return <Loading />;
  }

  if (!data?.account?.savedView) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.SAVED_VIEW} />;
  }

  return (
    <SavedView
      savedView={{
        ...data.account.savedView,
        isDefault: true,
        hiddenFromPublic: false,
      }}
    />
  );
};
